import { Modal, Flex, Form, Input } from 'antd';
import { useEffect } from 'react';
import styles from './redirection-target-modal.module.scss';
import { Button } from '../button/button';
import { RedirectionTarget } from '@/services/redirection-targets-service/redirection-targets-service.types';

type RedirectionTargetModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (values: RedirectionTargetFormValues) => void;
  isLoading?: boolean;
  initialValues?: RedirectionTarget | null;
  mode: 'create' | 'edit';
};

export type RedirectionTargetFormValues = {
  name: string;
  phone: string;
  email?: string;
  extension?: string;
};

export function RedirectionTargetModal({
  open,
  onClose,
  onSave,
  isLoading = false,
  initialValues,
  mode,
}: RedirectionTargetModalProps) {
  const [form] = Form.useForm<RedirectionTargetFormValues>();

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialValues) {
        form.setFieldsValue({
          name: initialValues.name,
          phone: initialValues.phone,
          email: initialValues.email || undefined,
          extension: initialValues.extension || undefined,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, mode, initialValues, form]);

  const handleSubmit = (values: RedirectionTargetFormValues) => {
    onSave(values);
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      title={null}
      width={500}
      className={styles.modal}
    >
      <Flex vertical gap={20}>
        <div>
          <h3 className={styles.title}>
            {mode === 'create' ? 'Add New Redirection Target' : 'Edit Redirection Target'}
          </h3>
          <p className={styles.subtitle}>
            {mode === 'create'
              ? 'Create a new redirection target for call forwarding'
              : 'Update redirection target information'}
          </p>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
            rules={[
              { required: true, message: 'Please enter a phone number' },
              {
                pattern: /^\+?[0-9\s\-()]+$/,
                message: 'Please enter a valid phone number',
              },
            ]}
          >
            <Input placeholder="+1234567890" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="email@example.com" />
          </Form.Item>

          <Form.Item
            label="Extension"
            name="extension"
          >
            <Input placeholder="123" />
          </Form.Item>

          <Flex gap={12} justify="flex-end" style={{ marginTop: 24 }}>
            <Button buttonVariant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              buttonVariant="primary"
              onClick={() => form.submit()}
              loading={isLoading}
            >
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Modal>
  );
}
